import { MouseEvent } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faXmark } from '@fortawesome/pro-solid-svg-icons';
import toast from 'react-hot-toast';
import { useUserEmailModalViewModel } from '@client/new/community/view-model/userEmailModal.viewModel';

type UserEmailModalProps = {
  open: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  profilePicture?: string;
  fallbackEmail?: string | null;
};

export const UserEmailModal = ({
  open,
  onClose,
  userId,
  username,
  profilePicture,
  fallbackEmail,
}: UserEmailModalProps) => {
  const emailState = useUserEmailModalViewModel({
    userId,
    fallbackEmail,
    enabled: open,
  });
  const isLoading =
    emailState.type === 'fetching' || emailState.type === 'not-fetched';
  const hasError = emailState.type === 'fetch-error';
  const email = emailState.type === 'fetched' ? emailState.value.email : null;
  const allowEmails =
    emailState.type === 'fetched' ? emailState.value.allowEmails : null;
  const isUnavailable =
    emailState.type === 'fetched' ? emailState.value.isUnavailable : false;

  const handleClose = (event: { stopPropagation?: () => void }) => {
    event.stopPropagation?.();
    onClose();
  };

  const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!email) return;
    navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard!');
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          px: 4,
        }}
        onClick={handleClose}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: 4,
            p: 3,
            width: '420px',
            maxWidth: '100%',
          }}
          onClick={(event) => event.stopPropagation()}
          data-testid="userEmailModal"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                User Profile
              </Typography>
              <Typography
                sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
              >
                #{userId}
              </Typography>
            </Box>
            <button
              aria-label="Close"
              style={{
                padding: 4,
                color: '#8b8b8b',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={handleClose}
              data-testid="userEmailModalClose"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            <Avatar alt={username} src={profilePicture || ''} />
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{username}</Typography>
              <Typography
                sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
              >
                #{userId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ borderTop: '1px solid #ECEDF2', pt: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Email</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {isLoading && <CircularProgress size={16} />}
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: email ? 'text.primary' : 'text.secondary',
                  }}
                  data-testid="userEmailModalEmail"
                >
                  {isLoading
                    ? 'Loading email...'
                    : email || 'Email not available'}
                </Typography>
              </Box>
              <Button
                variant="text"
                startIcon={<FontAwesomeIcon icon={faCopy} />}
                onClick={handleCopy}
                disabled={!email || isLoading}
                data-testid="userEmailModalCopy"
              >
                Copy
              </Button>
            </Box>
            {allowEmails !== null && (
              <Typography
                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}
              >
                Marketing: {allowEmails ? 'Yes' : 'No'}
              </Typography>
            )}
            {hasError && (
              <Typography
                sx={{ fontSize: '0.75rem', color: 'error.main', mt: 1 }}
              >
                Unable to load email right now. Please try again later.
              </Typography>
            )}
            {isUnavailable && !hasError && (
              <Typography
                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}
              >
                This user has not shared an email.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
